import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  EzTuxEnvironment,
  EzTuxParentApplication,
  PodEventEmission,
} from '@jfw/types';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'jfw-wholesale-data-pod',
  imports: [MatDialogModule, MatIconModule, MatProgressSpinner],
  templateUrl: './data-pod.component.html',
  styleUrl: './data-pod.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WholesaleDataPodComponent {
  private _data = new BehaviorSubject<any>(null);
  @Input()
  set data(value: any) {
    this._data.next(value);
  }
  get data(): Observable<any> {
    return this._data.asObservable();
  }

  // EXT-6593: signal, not a plain field -- read directly in the template
  // and written from the data input's .subscribe() callback.
  exampleDataValue = signal<string | undefined>(undefined);

  private _showBottomArrow: boolean = false;
  // EXT-6593v2: signal-backed, not a plain field -- data-pods.component.ts
  // (the parent) writes these directly on sibling instances via
  // @ViewChildren rather than through a template binding, so a plain field
  // write never marked this OnPush component's view dirty. A signal
  // self-notifies regardless of which code performs the write.
  private _active = signal(false);
  private _enableSpinner = signal(false);
  private _contenttext = signal<string | undefined>(undefined);
  private _podtitle = signal<string | undefined>(undefined);
  private _arrowIgnoresSpinner: boolean = false;
  private _loadingerror: string = '';

  // @Input() injectedRouter: Router | undefined;
  @Input() id: string | undefined;
  @Input() headerlabel: string | undefined;

  @Input()
  set podtitle(value: string | undefined) {
    this._podtitle.set(value);
  }
  get podtitle(): string | undefined {
    return this._podtitle();
  }

  @Input()
  set contenttext(value: string | undefined) {
    this._contenttext.set(value);
  }
  get contenttext(): string | undefined {
    return this._contenttext();
  }

  @Input() contenttextstyleoverride: string | undefined;
  @Input() contentimagesrc: string | undefined;
  @Input() contentimagealt: string | undefined;
  @Input() contentimagestyleoverride: string | undefined;
  @Input() contentimageclass: string | undefined;
  @Input() theme: string | undefined;
  @Input() parent_application: EzTuxParentApplication;
  @Input() environment: EzTuxEnvironment;

  @Input()
  set showbottomarrow(value: string | boolean) {
    this._showBottomArrow = value === true || value === 'true';
  }
  get showbottomarrow(): boolean {
    return this._showBottomArrow;
  }

  @Input()
  set active(value: string | boolean) {
    this._active.set(value === true || value === 'true');
  }
  get active(): boolean {
    return this._active();
  }

  @Input()
  set enablespinner(value: string | boolean) {
    this._enableSpinner.set(value === true || value === 'true');
  }
  get enablespinner(): boolean {
    return this._enableSpinner();
  }

  @Input()
  set arrowIgnoresSpinner(value: string | boolean) {
    this._arrowIgnoresSpinner = value === true || value === 'true';
  }
  get arrowIgnoresSpinner(): boolean {
    return this._arrowIgnoresSpinner;
  }

  @Input()
  set loadingerror(value: string) {
    this._loadingerror = value;
  }
  get loadingerror(): string {
    return this._loadingerror;
  }

  @Output() podClick = new EventEmitter<PodEventEmission>();
  @Output() podHoverEnter = new EventEmitter<PodEventEmission>();
  @Output() podHoverLeave = new EventEmitter<PodEventEmission>();

  contentClass: string = 'content';

  subscription = new Subscription();

  constructor() {}

  ngOnInit(): void {
    if (this.contenttext !== undefined) {
      if (this.contenttext.length > 3) {
        this.contentClass = 'content-small';
      }
    }
    // console.log('Pod initialized => ngOnInit()');
    // console.log('showbottomarrow', this.showbottomarrow);

    // console.log('Pod parent_application:', this.parent_application);
    // console.log('Pod environment:', this.environment);

    /// Show type of this.showbottomarrow
    // console.log('this.showbottomarrow', typeof this.showbottomarrow);

    /// Subscription for changes to the data property
    this.subscription.add(
      this.data.subscribe({
        next: (newValue: any) => {
          // console.log('Data changed:', newValue);

          if (newValue && newValue.newData !== undefined) {
            this.exampleDataValue.set(newValue.newData);
          }
          // Handle the new value as needed
        },
        error: (err) => console.error('Error in data subscription:', err),
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // console.log('ngOnChanges called with changes:', changes);
    // console.log('Changes detected:', changes);
  }

  ngOnDestroy(): void {
    // console.log('Pod destroyed => ngOnDestroy()');
    this.subscription.unsubscribe(); // Unsubscribe to avoid memory leaks
  }

  displayBottomArrows(): boolean {
    const contentAvailable =
      this.contenttext !== undefined &&
      this.contenttext !== null &&
      this.contenttext !== '' &&
      this.contenttext !== '!';

    // console.log('displayBottomArrows called');
    // console.log('contenttext:', '#' + this.contenttext + '#');
    // console.log('contentAvailable:', contentAvailable);

    if (
      contentAvailable &&
      ((this.showbottomarrow && this.enablespinner === false) ||
        (this.showbottomarrow &&
          this.enablespinner === true &&
          this.arrowIgnoresSpinner) ||
        (this.showbottomarrow && this.enablespinner))
    ) {
      return true;
    }

    return false;
  }

  handleClick(event: PointerEvent | MouseEvent): void {
    //console.log('Pod clicked => handleClick()');
    //console.log(event);

    const podEventEmission: PodEventEmission = {
      podEvent: event,
      podId: this.id ?? 'undefined',
      podEventType: 'click',
    };
    this.podClick.emit(podEventEmission); // Emit the click event to the parent
  }

  handleHover(event: PointerEvent | MouseEvent): void {
    // console.log('Pod hovered => handleHover()');
    // console.log(event);

    const podEventEmission: PodEventEmission = {
      podEvent: event,
      podId: this.id ?? 'undefined',
      podEventType: event.type === 'mouseenter' ? 'mouseenter' : 'mouseleave',
    };

    if (event.type === 'mouseenter') {
      // console.log('Mouse entered the pod');
      // Emit the hover enter event
      this.podHoverEnter.emit(podEventEmission);
    } else if (event.type === 'mouseleave') {
      //console.log('Mouse left the pod');
      this.podHoverLeave.emit(podEventEmission);
    }
  }

  clearExampleDataValue(): void {
    this.exampleDataValue.set(undefined);
  }
}
